import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ChevronRight, PlusIcon } from 'lucide-react';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { ProjectAvatar } from '@/shared/components/custom/avatars/ProjectAvatar';
import { PALETTE } from '@/core/lib/colors';
import { ClientMembersFilters } from './ClientMembersFilters';

function resolveClientColor(id: string): string {
  const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

interface ClientMembersToolbarProps {
  clientName?: string;
  search: string;
  onSearchChange: (value: string) => void;
  activeFiltersCount: number;
  onResetFilters: () => void;
  onInviteClick: () => void;
}

export function ClientMembersToolbar({
  clientName,
  search,
  onSearchChange,
  activeFiltersCount,
  onResetFilters,
  onInviteClick,
}: ClientMembersToolbarProps) {
  const { t } = useTranslation('client-members');
  const { orgId, clientId } = useParams<{ orgId: string; clientId: string }>();

  return (
    <Toolbar className="pt-0">
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-2">
          <ProjectAvatar
            name={clientName ?? ''}
            color={resolveClientColor(clientId ?? '')}
            size="sm"
          />
          <Link
            to={`/${orgId}/clients`}
            className="text-xl font-semibold text-muted-foreground hover:text-foreground transition-colors shrink-0"
          >
            {clientName ?? '…'}
          </Link>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="text-xl font-semibold text-foreground truncate">
            {t('breadcrumb.members')}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          {clientName ? t('page.subtitleWithName', { clientName }) : t('page.subtitle')}
        </p>
      </div>
      <Toolbar.Actions>
        <ClientMembersFilters
          search={search}
          onSearchChange={onSearchChange}
          activeFiltersCount={activeFiltersCount}
          onReset={onResetFilters}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="default"
              onClick={onInviteClick}
              aria-label={t('toolbar.invite')}
              data-testid="client-members-invite-button"
            >
              <PlusIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('toolbar.invite')}</TooltipContent>
        </Tooltip>
      </Toolbar.Actions>
    </Toolbar>
  );
}
